/**
 * Camlin Companion — proactive engagement orchestrator (doc 116 §2.4/§7 D3,
 * CO2).
 *
 * A trigger firing (real DOM detector, `proactiveTriggers.ts`) is
 * necessary but not sufficient for a nudge to show. Defense in depth per
 * §2.4 ("caps are also enforced server-side per visitor session so a
 * client bug can't nag"): BOTH the local evaluator (`proactive.ts`'s
 * `evaluateProactiveNudge` — fast, works offline, the primary UX gate) and
 * the server (`engagementClient.ts` — the control, can't be bypassed by a
 * buggy or tampered client) must agree the nudge is allowed. The local
 * check runs FIRST and short-circuits before any network call — a client
 * already at its page/session cap or in a dismiss cool-off has no reason
 * to bother the control plane.
 */

import {
  evaluateProactiveNudge,
  recordProactiveNudgeShown,
  type CompanionProactiveBlockReason,
  type CompanionProactiveSessionState,
  type CompanionProactiveStorage
} from './proactive';
import type { CompanionEngagementClient } from './engagementClient';
import type { CompanionProactiveCaps } from './types';

export interface EvaluateAndShowProactiveNudgeParams {
  pagePath: string;
  caps: CompanionProactiveCaps;
  session: CompanionProactiveSessionState;
  storage: CompanionProactiveStorage;
  engagementClient: Pick<CompanionEngagementClient, 'attemptNudge'>;
  siteKey: string;
  visitorId: string;
  sessionId?: string;
  triggerMatched: boolean;
  now?: Date;
}

export interface EvaluateAndShowProactiveNudgeResult {
  allowed: boolean;
  reason?: CompanionProactiveBlockReason;
  /** The session, advanced with the shown nudge, when allowed — callers must persist this back (mirrors `recordProactiveNudgeShown`'s immutable-update contract). */
  nextSession: CompanionProactiveSessionState;
}

/**
 * Evaluates a fired trigger against the local caps first (no network call
 * on a local refusal), then against the server's independent copy of the
 * same policy. Returns `allowed: true` only when both agree.
 */
export async function evaluateAndShowProactiveNudge(
  params: EvaluateAndShowProactiveNudgeParams
): Promise<EvaluateAndShowProactiveNudgeResult> {
  const localEvaluation = evaluateProactiveNudge({
    pagePath: params.pagePath,
    caps: params.caps,
    session: params.session,
    storage: params.storage,
    now: params.now,
    triggerMatched: params.triggerMatched
  });

  if (!localEvaluation.allowed) {
    return { allowed: false, reason: localEvaluation.reason, nextSession: params.session };
  }

  const serverEvaluation = await params.engagementClient.attemptNudge({
    siteKey: params.siteKey,
    visitorId: params.visitorId,
    sessionId: params.sessionId ?? params.session.sessionId,
    pagePath: params.pagePath
  });

  if (!serverEvaluation.allowed) {
    return { allowed: false, reason: serverEvaluation.reason, nextSession: params.session };
  }

  return {
    allowed: true,
    nextSession: recordProactiveNudgeShown(params.session, params.pagePath)
  };
}
